import type { QueryClient } from '@tanstack/react-query';
import type { PositionStaffingCommandServerPort } from '../commands/PositionStaffingCommand';
import type { PositionStaffingIntent, PositionStaffingResult } from '../models/PositionStaffingIntent';
import type { PositionStructureIntent } from '../models/PositionStructureIntent';
import type { OrgChartEditBatchIntent } from '../models/OrgChartEditBatchIntent';
import { orgPlanningEditingContextKey, useOrgPlanningScenarioStore } from '../stores/orgPlanningScenarioStore';
import { successionOptimisticContext } from '../stores/successionOptimisticSlateStore';
import { enqueueOrgPlanningTransition } from './orgPlanningCommandSequencer';
import { PositionCommandSettlementError } from './positionCommandSettlement';
import { captureOrgPlanningReviewScope, isOrgPlanningReviewScopeCurrent } from './useOrgPlanningReviewScope';
import type { ICommand } from '../../../core/commands/ICommand';
import { assertAtlasDraftHistoryAllowed, completeAtlasDraftSave, trackAtlasDraftCanvasRefresh, trackAtlasDraftSave, useAtlasDraftSaveRecoveryStore } from '../stores/atlasDraftSaveRecoveryStore';
import { acknowledgeDraftHistoryRestore, draftHistoryEquivalent } from './orgPlanningDraftHistory';
import { atlasReviewBlocksEditing } from '../stores/atlasChangesStore';

type DraftIntent = PositionStaffingIntent | PositionStructureIntent | OrgChartEditBatchIntent;
export type DraftHistoryAction = 'discard' | 'restore-previous-draft-revision';

/** Shared identity/selection fence for semantic staffing and structure commands.
 * No protected values are persisted to browser storage. A dispatched request is
 * frozen: retry cannot silently change its target, graph version, or intent ID.
 */
export function createOrgPlanningDraftCommandPort<T extends DraftIntent>(input: {
  folderId: string;
  diagramId: string;
  initial: T;
  queryClient: QueryClient;
  submit: (intent: T, signal: AbortSignal) => Promise<PositionStaffingResult>;
  transition: (head: string, action: DraftHistoryAction, signal: AbortSignal, restoreChangeSetId?: string) => Promise<string | null>;
  /** Runs for a validated staged receipt before its head is adopted, so adopting surfaces find primed data. */
  prime?: (result: PositionStaffingResult) => void;
  onAccepted?: (head: string, scenarioId: string) => void;
  onNoop?: () => void;
  onError?: (error: unknown) => void;
}): PositionStaffingCommandServerPort {
  const { folderId, diagramId, queryClient } = input;
  const scope = captureOrgPlanningReviewScope(folderId, diagramId);
  const initialSelection = useOrgPlanningScenarioStore.getState();
  const epoch = initialSelection.editEpochByDiagram[diagramId] ?? 0;
  const editingContext = orgPlanningEditingContextKey(diagramId, initialSelection);
  const original = structuredClone(input.initial);
  let dispatched: T | undefined;
  let accepted: PositionStaffingResult | undefined;
  let expectedHead: string | undefined;
  let scenarioId: string | undefined;
  let undone = false;
  let emptiedHead: string | undefined;
  let historyAttempt: { action: 'undo' | 'redo'; sourceHead: string; predecessor?: string; restoreHead?: string } | undefined;

  const conflict = () => new PositionCommandSettlementError('http_error', 409, 'scenario_revision_conflict');
  const current = (recoverDispatchedRequest = false) => {
    if (!isOrgPlanningReviewScopeCurrent(scope)) {
      throw new PositionCommandSettlementError('http_error', 403, 'authorization_denied');
    }
    const state = useOrgPlanningScenarioStore.getState();
    if (!recoverDispatchedRequest && (state.editEpochByDiagram[diagramId] ?? 0) !== epoch) throw conflict();
    return state;
  };
  const requireHead = () => {
    const selected = current().selectedByDiagram[diagramId];
    if (draftHistoryEquivalent(scope, epoch, selected?.headChangeSetId) !== draftHistoryEquivalent(scope, epoch, expectedHead)
      || (expectedHead && selected?.scenarioId !== scenarioId)) throw conflict();
    return selected?.headChangeSetId;
  };
  const publish = async (head: string | undefined, savedScenarioId: string | undefined, predecessor: string | undefined) => {
    if (!isOrgPlanningReviewScopeCurrent(scope)) return;
    // A stale in-flight list must not rewind the receipt we are about to adopt.
    const key = ['atlas', 'organization-plans', folderId, diagramId];
    await queryClient.cancelQueries({ queryKey: key });
    await queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' });
    if (!isOrgPlanningReviewScopeCurrent(scope)) return;
    const state = useOrgPlanningScenarioStore.getState();
    if (head && savedScenarioId) state.bindEditingContext(diagramId, savedScenarioId, editingContext);
    const selected = state.selectedByDiagram[diagramId];
    const canAdvance = selected?.headChangeSetId === predecessor
      || (head !== undefined && selected?.headChangeSetId === head && selected.scenarioId === savedScenarioId);
    const adopted = canAdvance && (head && savedScenarioId
      ? state.adopt(diagramId, { scenarioId: savedScenarioId, headChangeSetId: head }, epoch)
      : state.adoptEmpty(diagramId, epoch));
    if (adopted && head && savedScenarioId) {
      trackAtlasDraftCanvasRefresh(scope, head);
      input.onAccepted?.(head, savedScenarioId);
    }
    void queryClient.invalidateQueries({ queryKey: key, refetchType: 'active' });
    void queryClient.invalidateQueries({
      queryKey: ['atlas', 'organization-plan-review', folderId, diagramId], refetchType: 'none',
    });
  };
  const receive = async (result: PositionStaffingResult, sent: T) => {
    if (result.intentId === sent.intentId && result.outcome === 'noop'
      && result.changeSetId === null && Number.isFinite(result.graphVersion)) {
      accepted = result;
      if (isOrgPlanningReviewScopeCurrent(scope)
        && (useOrgPlanningScenarioStore.getState().editEpochByDiagram[diagramId] ?? 0) === epoch) input.onNoop?.();
      return;
    }
    if (result.intentId !== sent.intentId || result.outcome !== 'staged'
      || !result.changeSetId || !result.scenarioId || !Number.isFinite(result.graphVersion)
      || result.supersededChangeSetId !== (sent.scenarioChangeSetId ?? null)
      || (sent.scenarioId && result.scenarioId !== sent.scenarioId)) {
      throw new PositionCommandSettlementError('http_error', 502, 'malformed_response');
    }
    accepted = result;
    expectedHead = result.changeSetId;
    scenarioId = result.scenarioId;
    if (isOrgPlanningReviewScopeCurrent(scope)) input.prime?.(result);
    await publish(expectedHead, scenarioId, sent.scenarioChangeSetId);
  };
  const serialized = async (operation: (signal: AbortSignal) => Promise<void>) => {
    try {
      await enqueueOrgPlanningTransition(successionOptimisticContext(folderId, diagramId), operation, queryClient);
    } catch (error) {
      if (isOrgPlanningReviewScopeCurrent(scope)) input.onError?.(error);
      throw error;
    }
  };

  const reverse = async (action: 'undo' | 'redo') => {
    const attemptId = `history:${original.intentId}:${action}`;
    assertAtlasDraftHistoryAllowed(scope, attemptId);
    if (historyAttempt && historyAttempt.action !== action) throw conflict();
    try {
      await serialized(async (signal) => {
        assertAtlasDraftHistoryAllowed(scope, attemptId);
        // Exact dispatched retries may reconcile after a deliberate selection
        // change, but publish still fences adoption by the original epoch.
        current(Boolean(historyAttempt));
        if (!accepted || accepted.outcome === 'noop' || (action === 'undo' ? undone : !undone)) return;
        if (!historyAttempt) {
          const predecessor = requireHead();
          const sourceHead = predecessor ?? (action === 'redo' ? emptiedHead : undefined);
          if (!sourceHead) throw conflict();
          historyAttempt = { action, sourceHead, predecessor,
            restoreHead: action === 'undo' ? accepted.supersededChangeSetId ?? undefined : accepted.changeSetId ?? undefined };
        }
        const attempt = historyAttempt;
        trackAtlasDraftSave(attemptId, scope, recoveryCommand, 'saving', undefined, undefined, action);
        const restored = await input.transition(attempt.sourceHead,
          attempt.restoreHead ? 'restore-previous-draft-revision' : 'discard', signal, attempt.restoreHead);
        if (!restored) throw new PositionCommandSettlementError('http_error', 502, 'malformed_response');
        expectedHead = attempt.restoreHead ? restored : undefined;
        if (attempt.restoreHead) acknowledgeDraftHistoryRestore(scope, epoch, restored, attempt.restoreHead);
        else emptiedHead = attempt.sourceHead;
        await publish(expectedHead, scenarioId, attempt.predecessor);
        undone = action === 'undo';
        historyAttempt = undefined;
        completeAtlasDraftSave(attemptId);
      });
    } catch (error) {
      if (historyAttempt) {
        const definite = error instanceof PositionCommandSettlementError && error.code === 'http_error'
          && error.status !== undefined && error.status >= 400 && error.status < 500
          && error.serverCode !== 'intent_in_progress';
        if (definite && !useAtlasDraftSaveRecoveryStore.getState().attempts[attemptId]?.outcomeUnknown) {
          historyAttempt = undefined;
          completeAtlasDraftSave(attemptId);
        } else trackAtlasDraftSave(attemptId, scope, recoveryCommand, 'unknown', undefined, undefined, action);
      }
      throw error;
    }
  };
  const port: PositionStaffingCommandServerPort = {
    historyRecoveryId: original.intentId,
    execute: async () => {
      if (atlasReviewBlocksEditing(scope)) throw new Error('Return to editing from the pending review before making another change.');
      // Unconfirmed work owns its original head until reconciled. A second
      // gesture cannot fork that work or turn a timeout into a new request.
      const pending = Object.values(useAtlasDraftSaveRecoveryStore.getState().attempts);
      const ownIndex = pending.findIndex((attempt) => attempt.id === original.intentId);
      const other = pending.find((attempt, index) =>
        (ownIndex < 0 || index < ownIndex) && attempt.phase !== 'saving'
        && attempt.scope.folderId === folderId && attempt.scope.diagramId === diagramId
        && isOrgPlanningReviewScopeCurrent(attempt.scope));
      if (other) throw new Error('Resolve the earlier unsaved edit before adding another change.');
      trackAtlasDraftSave(original.intentId, scope, recoveryCommand, 'saving');
      try {
        await serialized(async (signal) => {
      if (atlasReviewBlocksEditing(scope)) throw new Error('Return to editing from the pending review before making another change.');
      const selected = current(Boolean(dispatched)).selectedByDiagram[diagramId];
      if (Object.values(useAtlasDraftSaveRecoveryStore.getState().attempts).some((attempt) => attempt.historyAction)) {
        assertAtlasDraftHistoryAllowed(scope, original.intentId);
      }
      if (accepted) return;
      if (!dispatched) {
        // Only acknowledgements in this same editing epoch can advance the
        // predecessor while we wait. Selecting another plan is never a retry.
        if (original.scenarioId && original.scenarioId !== selected?.scenarioId) throw conflict();
        dispatched = {
          ...original,
          scenarioId: selected?.scenarioId,
          scenarioChangeSetId: selected?.headChangeSetId,
          expectedHeadChangeSetId: selected?.headChangeSetId,
        };
        if (dispatched.kind === 'edit_batch') {
          dispatched.intents = dispatched.intents.map((child) => ({ ...child,
            scenarioId: dispatched?.scenarioId, scenarioChangeSetId: dispatched?.scenarioChangeSetId,
            expectedHeadChangeSetId: dispatched?.expectedHeadChangeSetId,
          }));
        }
      }
      await receive(await input.submit(dispatched, signal), dispatched);
        });
        completeAtlasDraftSave(original.intentId);
      } catch (error) {
        const definite = !dispatched || error instanceof PositionCommandSettlementError && error.code === 'http_error'
          && error.status !== undefined && error.status >= 400 && error.status < 500
          && error.serverCode !== 'intent_in_progress';
        trackAtlasDraftSave(original.intentId, scope, recoveryCommand, definite ? 'failed' : 'unknown');
        throw error;
      }
    },
    undo: () => reverse('undo'),
    redo: () => reverse('redo'),
  };
  const recoveryCommand: ICommand = { id: original.intentId, name: 'Save organization draft edit', timestamp: Date.now(),
    historyRecoveryId: original.intentId,
    mutationDestination: 'protected_plan', execute: port.execute, undo: port.undo, redo: port.redo };
  return port;
}
